import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { favoritesApi, formatPrice, formatSurface, propertyTypes, transactionTypes } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Heart, MapPin, Maximize2, BedDouble, Bath, Car, Eye } from 'lucide-react';
import { toast } from 'sonner';

export const PropertyCard = ({ property, isFavorite = false, onFavoriteChange }) => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [favorite, setFavorite] = useState(isFavorite);
  const [loading, setLoading] = useState(false);

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour sauvegarder' : 'سجل الدخول للحفظ');
      return;
    }

    setLoading(true);
    try {
      if (favorite) {
        await favoritesApi.remove(property.id);
        toast.success(language === 'fr' ? 'Retiré des favoris' : 'تمت الإزالة من المفضلة');
      } else {
        await favoritesApi.add(property.id);
        toast.success(language === 'fr' ? 'Sauvegardé !' : 'تم الحفظ!');
      }
      setFavorite(!favorite);
      onFavoriteChange?.(!favorite);
    } catch (error) {
      toast.error(language === 'fr' ? 'Une erreur est survenue' : 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const isRent = property.transaction_type === 'location';
  const typeLabel = propertyTypes[language]?.[property.property_type] || property.property_type;

  return (
    <Link to={`/property/${property.id}`} data-testid={`property-card-${property.id}`}>
      <Card className="property-card group overflow-hidden border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.12)] bg-white">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={property.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Subtle vignette overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
            <Badge className={`${isRent ? 'bg-emerald-600' : 'bg-rose-600'} text-white font-semibold`}>
              {isRent ? (language === 'fr' ? 'Location' : 'إيجار') : (language === 'fr' ? 'Vente' : 'بيع')}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              className={`h-9 w-9 p-0 rounded-full bg-white shadow-md ${favorite ? 'text-rose-600' : 'text-slate-400 hover:text-rose-600'}`}
              onClick={handleFavorite}
              disabled={loading}
              data-testid={`favorite-btn-${property.id}`}
            >
              <Heart className={`w-5 h-5 ${favorite ? 'fill-current' : ''}`} />
            </Button>
          </div>

          {/* Type badge */}
          <div className="absolute bottom-3 left-3">
            <Badge variant="secondary" className="bg-white/95 text-slate-700 font-medium">
              {typeLabel}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <CardContent className="p-4 space-y-3">
          {/* Price */}
          <div className="flex items-baseline justify-between">
            <span className="font-manrope font-extrabold text-xl text-blue-900">
              {formatPrice(property.price, isRent)}
            </span>
            {property.price_per_sqm && !isRent && (
              <span className="text-xs text-slate-500">
                {new Intl.NumberFormat('fr-DZ').format(Math.round(property.price_per_sqm))} {t('prop_price_sqm')}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-semibold text-slate-900 line-clamp-1 group-hover:text-blue-900 transition-colors">
            {property.title}
          </h3>

          {/* Location */}
          <div className="flex items-center gap-1.5 text-sm text-slate-500">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="truncate">{property.neighborhood}, {property.city}</span>
          </div>

          {/* Features */}
          <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <Maximize2 className="w-4 h-4 text-slate-400" />
              <span>{formatSurface(property.surface)}</span>
            </div>
            {property.rooms > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <span>{property.rooms} {t('prop_rooms')}</span>
              </div>
            )}
            {property.bedrooms > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <BedDouble className="w-4 h-4 text-slate-400" />
                <span>{property.bedrooms}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
